import { useEffect, useState } from "react";
import { Button, Input, Popover, Skeleton, message } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import ColorPicker from "../../../../brand/ColorPicker";
import { ConfirmDeleteColorModal } from "../../../../brand/ConfirmDeleteColorModal";
import BrandKitApi from "../../../../../api/BrandKitApi";
import { BrandColorDto, BrandKitDto } from "../../../../../generated/dashboard-client";
import "./ColorsTab.scss";

export function ColorsTab({ selected }: { selected: BrandKitDto }) {
  const [colors, setColors] = useState<BrandColorDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPickerColor, setCurrentPickerColor] = useState("#ffffff");
  const [colorName, setColorName] = useState("");
  const [isColorPickerVisible, setIsColorPickerVisible] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentColor, setCurrentColor] = useState<BrandColorDto>({} as BrandColorDto);

  const [editingColor, setEditingColor] = useState<BrandColorDto | null>(null);
  const [editPickerColor, setEditPickerColor] = useState("");
  const [editColorName, setEditColorName] = useState("");

  useEffect(() => {
    if (!selected.id) return;
    setLoading(true);
    BrandKitApi.getColors(selected.id)
      .then((resp) => setColors(resp.data || []))
      .catch(() => message.error("Failed to load colors"))
      .finally(() => setLoading(false));
  }, [selected.id]);

  function handleColorPickerVisible(open: boolean) {
    if (!open && isColorPickerVisible) {
      onColorSave(currentPickerColor, colorName.trim() || undefined);
      setColorName("");
    }
    setIsColorPickerVisible(open);
  }

  function onColorSave(color: string, name?: string) {
    if (!selected.id) return;
    if (colors.some((c) => c.colorCode === color)) return;
    setColors([{ colorCode: color, name }, ...colors]);
    BrandKitApi.addColor(selected.id, color, name)
      .then((resp) => setColors(resp.data || []))
      .catch(() => {
        setColors(colors);
        message.error("Failed to add color");
      });
  }

  function onDeleteColor(color: BrandColorDto) {
    setCurrentColor(color);
    setDeleteModalOpen(true);
  }

  function confirmDelete() {
    if (!selected.id) return;
    setDeleting(true);
    BrandKitApi.deleteColor(selected.id, currentColor.colorCode!)
      .then((resp) => {
        setColors(resp.data || []);
        setDeleteModalOpen(false);
      })
      .catch(() => message.error("Failed to delete color"))
      .finally(() => setDeleting(false));
  }

  function openEditColor(color: BrandColorDto) {
    setEditingColor(color);
    setEditPickerColor(color.colorCode || "#ffffff");
    setEditColorName(color.name || "");
  }

  function handleEditColorClose(open: boolean) {
    if (!open && editingColor) {
      const oldCode = editingColor.colorCode!;
      const newCode = editPickerColor;
      const newName = editColorName.trim() || undefined;
      const changed = oldCode !== newCode || (editingColor.name || "") !== (newName || "");
      if (changed && selected.id) {
        setColors((prev) => prev.map((c) =>
          c.colorCode === oldCode ? { colorCode: newCode, name: newName } : c
        ));
        BrandKitApi.updateColor(selected.id, oldCode, newCode, newName)
          .then((resp) => setColors(resp.data || []))
          .catch(() => {
            message.error("Failed to update color");
            BrandKitApi.getColors(selected.id!)
              .then((resp) => setColors(resp.data || []));
          });
      }
      setEditingColor(null);
    }
  }

  const pickerContent = (
    <div className="brand-kit-color-picker">
      <ColorPicker onColorChange={setCurrentPickerColor} />
      <Input
        className="brand-kit-color-name-input"
        placeholder="Color name"
        value={colorName}
        onChange={(e) => setColorName(e.target.value)}
      />
    </div>
  );

  return (
    <div className="colors-tab">
      <div className="brand-kit-section-label">Colors</div>

      {loading ? (
        <Skeleton active />
      ) : (
        <ul className="brand-kit-colors">
          <li className="add">
            <Popover
              placement="bottomLeft"
              trigger="click"
              onOpenChange={handleColorPickerVisible}
              getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
              content={pickerContent}
            >
              <Button>+</Button>
            </Popover>
          </li>
          {colors.map((it) => (
            <li key={it.colorCode}>
              <Button
                className="rmv"
                type="link"
                onClick={() => onDeleteColor(it)}
              >
                <DeleteOutlined />
              </Button>
              <Popover
                placement="bottomLeft"
                trigger="click"
                open={editingColor?.colorCode === it.colorCode}
                onOpenChange={(open) => {
                  if (open) openEditColor(it);
                  else handleEditColorClose(false);
                }}
                content={
                  <div className="brand-kit-color-picker">
                    <ColorPicker onColorChange={setEditPickerColor} />
                    <Input
                      className="brand-kit-color-name-input"
                      placeholder="Color name"
                      value={editColorName}
                      onChange={(e) => setEditColorName(e.target.value)}
                    />
                  </div>
                }
              >
                <div
                  className="clr"
                  style={{ backgroundColor: it.colorCode, cursor: "pointer" }}
                />
              </Popover>
              <div className="color-info">
                {it.name && <span className="color-name">{it.name}</span>}
                <span className="color-hex">{it.colorCode}</span>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDeleteColorModal
        isVisible={deleteModalOpen}
        isLoading={deleting}
        onCancel={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
